/**
 * Order engine rules shared by the POS, the tests and the server (which re-validates everything).
 * Phase 3: placement, editing before send, send to production, cancel and void without money.
 * Payment capture, refunds, discounts and taxes arrive in Phase 4 and only extend these types.
 * Phase 6 adds the served states (`partially_served`, `served`) and table sessions (ADR-021).
 */

export const ORDER_TYPES = ['dine_in', 'takeout', 'advance'] as const;
export type OrderType = (typeof ORDER_TYPES)[number];

export const ORDER_STATUSES = ['open', 'completed', 'cancelled', 'voided'] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

export const PAYMENT_STATUSES = [
  'unpaid',
  'partially_paid',
  'paid',
  'written_off',
  'partially_refunded',
  'refunded',
] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

/**
 * Nothing more is owed: paid, or settled with a write-off (a walk-out, a staff meal or the owner's comp, ADR-031).
 * Mirror of `app.payment_settled`: completion, the pay-first send gate and reopening read both alike.
 */
export function isSettled(status: PaymentStatus): boolean {
  return status === 'paid' || status === 'written_off';
}

export const FULFILMENT_STATUSES = [
  'not_required',
  'not_sent',
  'sent',
  'in_progress',
  'partially_ready',
  'ready',
  'partially_served',
  'served',
] as const;
export type FulfilmentStatus = (typeof FULFILMENT_STATUSES)[number];

export const ORDER_EVENT_TYPES = [
  'order.placed',
  'order.updated',
  'order.line_added',
  'order.line_updated',
  'order.line_removed',
  'order.line_voided',
  'order.sent',
  'ticket.created',
  'ticket.cancelled',
  'order.cancelled',
  'order.voided',
  'payment.captured',
  'payment.voided',
  'refund.created',
  'receipt.issued',
  'order.completed',
  'order.reopened',
  'order.auto_send_failed',
  'order.written_off',
  'order.write_off_reinstated',
  'item.started',
  'item.ready',
  'item.undone',
  'item.recalled',
  'ticket.started',
  'ticket.ready',
  'ticket.undone',
  'ticket.recalled',
  'ticket.dismissed',
  'order.in_progress',
  'order.partially_ready',
  'order.ready',
  'ticket.served',
  'ticket.unserved',
  'order.partially_served',
  'order.served',
  'order.seated',
  'order.unseated',
  'order.table_cleared',
] as const;
export type OrderEventType = (typeof ORDER_EVENT_TYPES)[number];

export const MAX_LINE_QTY = 999;

/** What the cart sends: ids only; the server snapshots names, prices and routing. */
export interface CartModifier {
  modifier_option_id: string;
  qty?: number;
}
export interface CartLine {
  id?: string;
  product_variant_id: string;
  qty: number;
  notes?: string | null;
  modifiers?: CartModifier[];
}

/** Line total in minor units: (unit price + sum of modifier deltas) × qty. */
export function lineTotalMinor(
  unitPriceMinor: number,
  modifierDeltasMinor: readonly number[],
  qty: number,
): number {
  const perUnit = unitPriceMinor + modifierDeltasMinor.reduce((a, b) => a + b, 0);
  return perUnit * qty;
}

export interface OrderTotals {
  subtotal_minor: number;
  discount_total_minor: number;
  tax_total_minor: number;
  total_minor: number;
}

/** Tax-neutral totals (ADR-003): the total equals the sum of active lines. */
export function orderTotals(
  lines: readonly { line_total_minor: number; status?: 'active' | 'voided' }[],
): OrderTotals {
  const subtotal = lines
    .filter((l) => (l.status ?? 'active') === 'active')
    .reduce((a, l) => a + l.line_total_minor, 0);
  return {
    subtotal_minor: subtotal,
    discount_total_minor: 0,
    tax_total_minor: 0,
    total_minor: subtotal,
  };
}

/** `MMDD-<terminal code>-<sequence>` (ADR-004). Uniqueness lives on (terminal, business date, seq). */
export function formatOrderNumber(businessDate: string, terminalCode: string, seq: number): string {
  return `${businessDate.slice(5, 7)}${businessDate.slice(8, 10)}-${terminalCode}-${seq}`;
}

export const ORDER_NUMBER_PATTERN = /^(\d{4})-([A-Z0-9]{1,3})-(\d+)$/;
export function parseOrderNumber(
  text: string,
): { mmdd: string; terminalCode: string; seq: number } | null {
  const m = ORDER_NUMBER_PATTERN.exec(text.trim());
  if (!m) return null;
  return { mmdd: m[1]!, terminalCode: m[2]!, seq: Number(m[3]) };
}

/** A modifier group as the picker sees it: its rule and the options it owns. */
export interface ModifierGroupRule {
  id: string;
  name: string;
  min_select: number;
  max_select: number | null;
  option_ids: readonly string[];
}

/**
 * Validate the chosen options of one line against its product's groups. Returns the first problem
 * as a sentence, or null. Options from groups not on the product are rejected.
 */
export function validateLineSelection(
  groups: readonly ModifierGroupRule[],
  selected: readonly CartModifier[],
): string | null {
  const owner = new Map<string, ModifierGroupRule>();
  for (const g of groups) for (const id of g.option_ids) owner.set(id, g);
  const counts = new Map<string, number>();
  for (const s of selected) {
    const q = s.qty ?? 1;
    if (!Number.isInteger(q) || q < 1)
      return 'Modifier quantity must be a whole number of 1 or more';
    const g = owner.get(s.modifier_option_id);
    if (!g) return 'That option does not belong to this product';
    counts.set(g.id, (counts.get(g.id) ?? 0) + q);
  }
  for (const g of groups) {
    const n = counts.get(g.id) ?? 0;
    if (n < g.min_select) return `${g.name}: choose at least ${g.min_select}`;
    if (g.max_select !== null && n > g.max_select)
      return `${g.name}: choose at most ${g.max_select}`;
  }
  return null;
}

export function validateCartLine(line: CartLine): string | null {
  if (!Number.isInteger(line.qty) || line.qty < 1 || line.qty > MAX_LINE_QTY)
    return `Quantity must be between 1 and ${MAX_LINE_QTY}`;
  if (line.notes && line.notes.length > 200) return 'Notes must be 200 characters or fewer';
  return null;
}

/** Placement rules mirrored from the database checks. */
export function validateOrderHeader(input: {
  type: OrderType;
  dining_table_id?: string | null;
  scheduled_for?: string | null;
}): string | null {
  if (input.type === 'dine_in' && !input.dining_table_id) return 'Dine-in orders need a table';
  if (input.type === 'advance' && !input.scheduled_for)
    return 'Advance orders need a scheduled time';
  return null;
}

export type SendGate = 'allowed' | 'needs_permission';

/**
 * ADR-005: with `orders.require_payment_before_send` on, an unpaid order may be sent only by an
 * actor holding `order.send_unpaid` (ADR-022: the cashier and the owner); anyone else needs a
 * holder's PIN on the same terminal.
 */
export function sendGate(input: {
  requirePaymentBeforeSend: boolean;
  paymentStatus: PaymentStatus;
  actorCanSendUnpaid: boolean;
}): SendGate {
  if (!input.requirePaymentBeforeSend) return 'allowed';
  if (isSettled(input.paymentStatus)) return 'allowed';
  return input.actorCanSendUnpaid ? 'allowed' : 'needs_permission';
}

/* ── Timezone-aware scheduling (advance orders) ── */

function tzParts(instantMs: number, timeZone: string) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(instantMs));
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? '0');
  return {
    y: get('year'),
    mo: get('month'),
    d: get('day'),
    h: get('hour') === 24 ? 0 : get('hour'),
    mi: get('minute'),
    s: get('second'),
  };
}

/** Offset (ms) of `timeZone` from UTC at the given instant. */
function tzOffsetMs(instantMs: number, timeZone: string): number {
  const p = tzParts(instantMs, timeZone);
  return Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi, p.s) - instantMs;
}

/**
 * A wall-clock time typed by a person in the location's timezone ("2026-09-27T14:30") to an
 * instant in UTC (ISO string). Two passes handle offsets that change around DST transitions.
 * Returns null when the text is not a date-time.
 */
export function wallTimeToUtcIso(wall: string, timeZone: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(wall.trim());
  if (!m) return null;
  const [y, mo, d, h, mi, s] = [m[1], m[2], m[3], m[4], m[5], m[6] ?? '0'].map(Number) as number[];
  const asUtc = Date.UTC(y!, mo! - 1, d!, h!, mi!, s!);
  if (Number.isNaN(asUtc)) return null;
  // Date.UTC silently normalises overflow (month 13, hour 99); insist on the exact fields.
  const check = new Date(asUtc);
  if (
    check.getUTCFullYear() !== y ||
    check.getUTCMonth() + 1 !== mo ||
    check.getUTCDate() !== d ||
    check.getUTCHours() !== h ||
    check.getUTCMinutes() !== mi
  )
    return null;
  let guess = asUtc - tzOffsetMs(asUtc, timeZone);
  guess = asUtc - tzOffsetMs(guess, timeZone);
  return new Date(guess).toISOString();
}

/** The reverse: an instant shown as the location's wall time, in the input format. */
export function utcIsoToWallTime(iso: string, timeZone: string): string {
  const p = tzParts(new Date(iso).getTime(), timeZone);
  const two = (n: number) => String(n).padStart(2, '0');
  return `${p.y}-${two(p.mo)}-${two(p.d)}T${two(p.h)}:${two(p.mi)}`;
}

/** Human display in the location's timezone, e.g. "Sat 27 Sep, 2:30 pm". */
export function formatInTimeZone(iso: string, timeZone: string, locale = 'en-PH'): string {
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso));
}
